/**
 * The one number input. Replaces <input type="number"> and hand-formatted money boxes, which fought the person
 * typing: an emptied box snapped back to "0" (so "43" became "043"), the thousands separator was re-inserted on
 * every key (the caret jumped to the end), the mouse wheel changed values while scrolling, and fields that
 * recompute the plan saved half-typed numbers.
 *
 * While focused the box shows exactly what was typed (no reformatting, no clamping); leading zeros are dropped
 * and it can be emptied. It shows "125 000" style grouping when not focused, clamps to min/max when you leave,
 * steps with ↑/↓, and never reacts to the wheel. `commit="blur"` reports the number only when you leave the box
 * or press Enter — for fields whose change is expensive (a total or target that rebuilds the plan).
 */
import { useEffect, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';

type Native = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max' | 'step' | 'defaultValue'>;
export interface NumberFieldProps extends Native {
  value: number | null | undefined;
  /** The number typed; null when the box is empty (only when `allowEmpty`). */
  onChange: (n: number | null) => void;
  min?: number;
  max?: number;
  /** ↑/↓ step (and the decimals shown follow it: 0.05 → 2 decimals). */
  step?: number;
  /** Decimals kept; default from `step` (0 for whole numbers). */
  decimals?: number;
  /** Group thousands when not editing ("125 000"). Default true for whole numbers ≥ 10 000. */
  group?: boolean;
  /** Text after the number inside the box, e.g. "%" or "SEK". */
  suffix?: string;
  /** 'change' (default): every valid keystroke; 'blur': on leaving the box or Enter. */
  commit?: 'change' | 'blur';
  /** An empty box means "no value" (null) instead of reverting to the last number. */
  allowEmpty?: boolean;
  /** Classes for the wrapper (the box look); `className` goes to the <input>. */
  boxClassName?: string;
}

const decimalsOf = (step?: number) => (step && step < 1 ? Math.min(4, (String(step).split('.')[1] || '').length) : 0);
const round = (n: number, d: number) => (d ? Math.round(n * 10 ** d) / 10 ** d : Math.round(n));

export function formatNumber(n: number, decimals = 0, group = true): string {
  const s = decimals ? n.toFixed(decimals).replace(/\.?0+$/, '') : String(Math.round(n));
  if (!group) return s;
  const [int, frac] = s.split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')}${frac ? `.${frac}` : ''}`;
}
/** What was typed → a number (spaces and grouping ignored, comma or dot as the decimal mark); null when empty or unreadable. */
export function parseNumber(raw: string): number | null {
  const t = raw.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  if (!t || t === '-' || t === '.') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
/** Keep digits, one decimal mark and (when negatives are allowed) a leading minus; drop leading zeros ("043" → "43", "0.5" stays). */
export function cleanTyped(raw: string, opts: { decimals: number; negative: boolean }): string {
  let t = raw.replace(/[^\d.,-]/g, '');
  const neg = opts.negative && t.startsWith('-');
  t = t.replace(/-/g, '');
  if (opts.decimals) {
    const i = t.search(/[.,]/);
    if (i >= 0) t = `${t.slice(0, i)}${t[i]}${t.slice(i + 1).replace(/[.,]/g, '').slice(0, opts.decimals)}`;
  } else t = t.replace(/[.,]/g, '');
  t = t.replace(/^0+(?=\d)/, '');
  return `${neg ? '-' : ''}${t}`;
}

export function NumberField({ value, onChange, min, max, step = 1, decimals, group, suffix, commit = 'change', allowEmpty = false, boxClassName, className, onFocus, onBlur, onKeyDown, placeholder, disabled, readOnly, ...rest }: NumberFieldProps) {
  const dec = decimals ?? decimalsOf(step);
  const grouped = (n: number) => group ?? (!dec && Math.abs(n) >= 10000);
  const shown = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? '' : formatNumber(n, dec, grouped(n)));
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const last = useRef<number | null>(value ?? null);
  useEffect(() => { if (!focused) last.current = value ?? null; }, [value, focused]);

  const clamp = (n: number) => round(Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n)), dec);
  const report = (n: number | null) => { if (n !== (value ?? null)) onChange(n); };
  const finish = () => {
    const n = parseNumber(draft);
    if (n == null) { if (allowEmpty) report(null); else if (commit === 'blur') { /* nothing typed: keep the value */ } else report(last.current); }
    else report(clamp(n));
  };
  const typed = (raw: string) => {
    const t = cleanTyped(raw, { decimals: dec, negative: (min ?? 0) < 0 });
    setDraft(t);
    if (commit !== 'change') return;
    const n = parseNumber(t);
    if (n == null) { if (allowEmpty) report(null); return; }
    // Report while typing, but never above max (a percentage box must not reach 430 on the way to 43.0).
    report(round(max != null ? Math.min(max, n) : n, dec));
  };
  const stepBy = (dir: 1 | -1, e: KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const base = parseNumber(draft) ?? value ?? min ?? 0;
    const n = clamp(base + dir * step * (e.shiftKey ? 10 : 1));
    setDraft(String(n)); if (commit === 'change') report(n);
  };

  return (
    <span className={boxClassName ?? 'relative flex items-center'}>
      <input
        {...rest}
        type="text"
        inputMode={dec ? 'decimal' : 'numeric'}
        autoComplete="off"
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        value={focused ? draft : shown(value)}
        onFocus={(e) => { setFocused(true); setDraft(value == null ? '' : String(round(value, dec))); last.current = value ?? null; onFocus?.(e); }}
        onChange={(e) => typed(e.target.value)}
        onBlur={(e) => { finish(); setFocused(false); onBlur?.(e); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
          else if (e.key === 'Escape') { setDraft(last.current == null ? '' : String(last.current)); if (commit === 'change' && last.current !== (value ?? null)) onChange(last.current); }
          else if (!readOnly && e.key === 'ArrowUp') stepBy(1, e);
          else if (!readOnly && e.key === 'ArrowDown') stepBy(-1, e);
          onKeyDown?.(e);
        }}
        className={`${className ?? ''}${suffix ? ' pr-8' : ''}`}
      />
      {suffix && <span className="pointer-events-none absolute right-2.5 text-[12px] text-[#667085]">{suffix}</span>}
    </span>
  );
}
